import { useState, useEffect } from 'react';
import { ArrowLeft, Trash2, Download, AlertTriangle, Shield, Eye, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { getAuthHeaders } from '../lib/supabase';
import { toSafeDisplayString } from '../lib/apiErrors';
import { clearJobMatchProBrowserData } from '../lib/jobMatchStorage';
import axios from 'axios';

const safeManageDataText = (value) => toSafeDisplayString(value, { context: 'editor-load' });

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GDPR_DELETE_SUCCESS_KEY = 'jobmatchpro:gdpr-delete-success';

// Track 26 — Recruiter Privacy (four independent, fail-closed consent gates)
const RECRUITER_CONSENT_KEYS = [
  'recruiter_discoverable',
  'cv_visible',
  'work_style_visible',
  'recruiter_contact_allowed',
];

const DEFAULT_RECRUITER_CONSENT = {
  recruiter_discoverable: false,
  recruiter_discoverable_updated_at: null,
  cv_visible: false,
  cv_visible_updated_at: null,
  work_style_visible: false,
  work_style_visible_updated_at: null,
  recruiter_contact_allowed: false,
  recruiter_contact_allowed_updated_at: null,
};

const RECRUITER_CONSENT_CONTROLS = [
  {
    key: 'recruiter_discoverable',
    label: 'Be discoverable by recruiters',
    description: 'Allow recruiters to find your profile. Off by default. Nothing below this is visible to recruiters unless this is on.',
  },
  {
    key: 'cv_visible',
    label: 'Share my CV with recruiters',
    description: 'Only applies while discoverability is on.',
  },
  {
    key: 'work_style_visible',
    label: 'Share my Work Style with recruiters',
    description: 'Only applies while discoverability is on. Your Work Style is never used to rank or reject you, regardless of this setting.',
  },
  {
    key: 'recruiter_contact_allowed',
    label: 'Allow recruiters to contact me',
    description: 'Only applies while discoverability is on.',
  },
];

const normalizeRecruiterConsent = (value) => {
  const source = value && typeof value === 'object' ? value : {};
  return RECRUITER_CONSENT_KEYS.reduce((next, key) => ({
    ...next,
    [key]: source[key] === true,
    [`${key}_updated_at`]: source[`${key}_updated_at`] || null,
  }), { ...DEFAULT_RECRUITER_CONSENT });
};

function RecruiterPrivacySection({ consent, isLoading, error, onRetry, pendingKeys, saveErrors, savedKey, onToggle }) {
  return (
    <section id="recruiter-privacy" className="border border-border p-6 scroll-mt-24" data-testid="recruiter-privacy-card" aria-labelledby="recruiter-privacy-heading">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 border border-foreground/10 bg-foreground/5 flex items-center justify-center" aria-hidden="true">
          <Eye className="w-5 h-5 text-foreground" />
        </div>
        <div>
          <h2 id="recruiter-privacy-heading" className="font-serif-display text-2xl font-medium mb-1">Recruiter Privacy</h2>
          <p className="text-sm text-muted-foreground" data-testid="recruiter-privacy-intro">
            These settings are optional. You can turn any of them off at any time. Completing Work Style, uploading a CV, or creating your account does not turn any of these on.
          </p>
        </div>
      </div>

      {isLoading && (
        <div className="space-y-3" data-testid="recruiter-privacy-loading" aria-live="polite">
          {[0, 1, 2, 3].map((item) => (
            <div key={item} className="h-16 border border-border bg-surface animate-pulse" />
          ))}
        </div>
      )}

      {!isLoading && error && (
        <div className="p-4 border border-red-500/30 bg-red-500/5" data-testid="recruiter-privacy-error" role="status">
          <p className="text-red-600 dark:text-red-400 text-sm">{safeManageDataText(error)}</p>
          <Button
            onClick={onRetry}
            className="btn-secondary mt-4 inline-flex items-center gap-2"
            data-testid="recruiter-privacy-retry-button"
          >
            <RefreshCw className="w-4 h-4" aria-hidden="true" />
            Retry
          </Button>
        </div>
      )}

      {!isLoading && !error && consent && (
        <div className="space-y-3" data-testid="recruiter-privacy-controls">
          {RECRUITER_CONSENT_CONTROLS.map((control) => {
            const checked = consent[control.key] === true;
            const isPending = pendingKeys.has(control.key);
            const inputId = `recruiter-consent-${control.key}`;
            const descriptionId = `${inputId}-description`;
            const errorId = `${inputId}-error`;
            const savedId = `${inputId}-saved`;
            return (
              <div key={control.key} className="border border-border bg-surface p-4" data-testid={`recruiter-consent-row-${control.key}`}>
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 flex-1">
                    <label htmlFor={inputId} className="font-medium" data-testid={`recruiter-consent-label-${control.key}`}>
                      {control.label}
                    </label>
                    <p id={descriptionId} className="mt-1 text-sm text-muted-foreground" data-testid={`recruiter-consent-description-${control.key}`}>
                      {control.description}
                    </p>
                    {saveErrors[control.key] && (
                      <p id={errorId} className="mt-2 text-sm text-red-600 dark:text-red-400" data-testid={`recruiter-consent-error-${control.key}`} role="status">
                        {safeManageDataText(saveErrors[control.key])}
                      </p>
                    )}
                    {savedKey === control.key && !saveErrors[control.key] && (
                      <p id={savedId} className="mt-2 text-sm text-green-700 dark:text-green-300" data-testid={`recruiter-consent-saved-${control.key}`} role="status">
                        This takes effect immediately.
                      </p>
                    )}
                  </div>
                  <div className="flex min-h-11 items-center gap-3 sm:justify-end">
                    <span className="text-sm font-medium" data-testid={`recruiter-consent-state-${control.key}`}>{checked ? 'On' : 'Off'}</span>
                    <input
                      id={inputId}
                      type="checkbox"
                      role="switch"
                      checked={checked}
                      disabled={isPending}
                      aria-checked={checked}
                      aria-describedby={[descriptionId, saveErrors[control.key] ? errorId : '', savedKey === control.key ? savedId : ''].filter(Boolean).join(' ')}
                      onChange={(event) => onToggle(control.key, event.target.checked)}
                      className="h-6 w-11 accent-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foreground disabled:cursor-not-allowed disabled:opacity-60"
                      data-testid={`recruiter-consent-toggle-${control.key}`}
                    />
                  </div>
                </div>
              </div>
            );
          })}
          <Link
            to="/dashboard/recruiter-profile-preview"
            className="mt-2 inline-flex min-h-11 items-center text-sm font-medium text-foreground underline"
            data-testid="recruiter-privacy-preview-link"
          >
            Preview what recruiters can see
          </Link>
        </div>
      )}
    </section>
  );
}

export default function ManageDataPage() {
  const { user, loading: authLoading, signOut } = useAuth();
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);

  // Track 23 — Account & Privacy summary
  const [accountOverview, setAccountOverview] = useState(null);
  const [accountOverviewLoading, setAccountOverviewLoading] = useState(false);
  const [accountOverviewError, setAccountOverviewError] = useState('');

  // Track 24 — Recent Activity
  const [activityEvents, setActivityEvents] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState('');

  // Track 26 — Recruiter Privacy
  const [recruiterConsent, setRecruiterConsent] = useState(null);
  const [recruiterConsentLoading, setRecruiterConsentLoading] = useState(false);
  const [recruiterConsentError, setRecruiterConsentError] = useState('');
  const [pendingRecruiterConsentKeys, setPendingRecruiterConsentKeys] = useState(() => new Set());
  const [recruiterConsentSaveErrors, setRecruiterConsentSaveErrors] = useState({});
  const [savedRecruiterConsentKey, setSavedRecruiterConsentKey] = useState('');

  useEffect(() => {
    if (user && !deleteSuccess) {
      fetchUserData();
      fetchAccountOverview();
      fetchActivity();
      fetchRecruiterConsent();
    }
  }, [user, deleteSuccess]);

  const fetchUserData = async () => {
    setIsLoading(true);
    setError('');
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(`${API}/gdpr/lookup`, { headers });
      if (response.data.found) {
        setUserData(response.data.data);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Session expired. Please sign in again.');
      } else {
        setError('Failed to load your data.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Independent of fetchUserData/gdpr-lookup on purpose — a failure here must
  // never disable or hide the existing Export/Delete controls.
  const fetchAccountOverview = async () => {
    setAccountOverviewLoading(true);
    setAccountOverviewError('');
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(`${API}/account/overview`, { headers });
      setAccountOverview(response.data);
    } catch (err) {
      setAccountOverviewError('Your career profile summary is temporarily unavailable.');
    } finally {
      setAccountOverviewLoading(false);
    }
  };

  // Independent of fetchUserData/fetchAccountOverview on purpose — a failure
  // here must never break the rest of Manage Data.
  const fetchActivity = async () => {
    setActivityLoading(true);
    setActivityError('');
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(`${API}/activity`, { headers });
      // Server already sorts; render verbatim, never re-sort/re-slice client-side.
      setActivityEvents(Array.isArray(response.data?.events) ? response.data.events : []);
    } catch (err) {
      setActivityError('Your recent activity is temporarily unavailable.');
    } finally {
      setActivityLoading(false);
    }
  };

  // Independent of the other three fetches on purpose — a failure here must
  // never break Account Overview, Recent Activity, Export, or Delete.
  const fetchRecruiterConsent = async () => {
    setRecruiterConsentLoading(true);
    setRecruiterConsentError('');
    setRecruiterConsentSaveErrors({});
    setSavedRecruiterConsentKey('');
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(`${API}/recruiter-consent/me`, { headers });
      setRecruiterConsent(normalizeRecruiterConsent(response.data));
    } catch (err) {
      // A failed GET is a distinct state from a confirmed all-OFF response —
      // never invent OFF locally when the load itself failed.
      setRecruiterConsent(null);
      setRecruiterConsentError(err.response?.status === 503
        ? 'Recruiter privacy settings are temporarily unavailable.'
        : "We couldn't load your recruiter privacy settings right now.");
    } finally {
      setRecruiterConsentLoading(false);
    }
  };

  const handleRecruiterConsentToggle = async (key, value) => {
    if (!RECRUITER_CONSENT_KEYS.includes(key) || pendingRecruiterConsentKeys.has(key)) return;
    // A Set (not a single key) tracks in-flight requests so toggling a
    // *different* control while one is still saving can never silently
    // un-protect the first control from a duplicate concurrent write.
    setPendingRecruiterConsentKeys((current) => new Set(current).add(key));
    setSavedRecruiterConsentKey('');
    setRecruiterConsentSaveErrors((current) => ({ ...current, [key]: '' }));
    try {
      const headers = await getAuthHeaders();
      const response = await axios.put(`${API}/recruiter-consent/me`, { key, value }, { headers });
      // Adopt only the confirmed server-returned state — never an optimistic
      // local flip before the request resolves.
      setRecruiterConsent(normalizeRecruiterConsent(response.data));
      setSavedRecruiterConsentKey(key);
    } catch (err) {
      setRecruiterConsentSaveErrors((current) => ({
        ...current,
        [key]: err.response?.status === 503
          ? 'Recruiter privacy settings are temporarily unavailable.'
          : 'Could not save this recruiter privacy setting.',
      }));
    } finally {
      setPendingRecruiterConsentKeys((current) => {
        const next = new Set(current);
        next.delete(key);
        return next;
      });
    }
  };

  const handleExport = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(`${API}/gdpr/export`, { headers });
      const dataStr = JSON.stringify(response.data, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `my-data-${Date.now()}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to export data. Please try again.');
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const headers = await getAuthHeaders();
      await axios.delete(`${API}/gdpr/delete`, { headers });
      clearJobMatchProBrowserData({ userId: user?.id, includeSession: true });
      try { window.sessionStorage.setItem(GDPR_DELETE_SUCCESS_KEY, '1'); } catch (_) { /* no-op */ }
      const signOutResult = await signOut();
      if (signOutResult?.error) throw signOutResult.error;
      setDeleteSuccess(true);
      setUserData(null);
      setRecruiterConsent(null);
      setShowDeleteConfirm(false);
    } catch (err) {
      setError('Failed to delete data. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  if (deleteSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center py-24">
        <div className="max-w-lg mx-auto px-6 text-center">
          <div className="w-20 h-20 bg-foreground/5 border border-foreground/20 flex items-center justify-center mx-auto mb-6">
            <Trash2 className="w-10 h-10 text-foreground" />
          </div>
          <h1 className="font-serif-display text-4xl tracking-tight font-medium mb-4">
            Data Deleted
          </h1>
          <p className="text-muted-foreground mb-8 text-lg">
            All your personal data, including your CV, has been permanently deleted from our systems. Locally stored drafts on this device were also removed.
          </p>
          <Link to="/" onClick={() => { try { window.sessionStorage.removeItem(GDPR_DELETE_SUCCESS_KEY); } catch (_) { /* no-op */ } }} className="btn-primary inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 md:py-24">
      <div className="max-w-2xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-12">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <h1 className="font-serif-display text-4xl md:text-5xl tracking-tight font-medium mb-4">
            Manage Your Data
          </h1>
          <p className="text-muted-foreground text-lg">
            Access, export, or delete your personal data in compliance with GDPR.
          </p>
        </div>

        {/* Loading */}
        {authLoading && (
          <div className="py-20 text-center text-muted-foreground">Loading...</div>
        )}

        {/* Signed in - loading data */}
        {user && isLoading && (
          <div className="py-20 text-center text-muted-foreground">Loading your data...</div>
        )}

        {/* Signed in - no data */}
        {user && !isLoading && !error && !userData && (
          <div className="p-8 border border-border bg-surface text-center" data-testid="no-data-message">
            <Shield className="w-8 h-8 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-medium text-lg mb-2">No data found</h3>
            <p className="text-muted-foreground text-sm mb-6">
              We don&rsquo;t have any submissions on file for <strong>{user.email}</strong>.
            </p>
            <Link to="/find-jobs">
              <Button className="btn-secondary">Submit Your Profile</Button>
            </Link>
          </div>
        )}

        {/* Signed in - data found */}
        {user && !isLoading && userData && (
          <div className="space-y-6" data-testid="user-data-section">
            <div className="p-4 border border-green-500/30 bg-green-500/5 flex items-center gap-3">
              <Shield className="w-5 h-5 text-green-600 dark:text-green-400" />
              <p className="text-green-700 dark:text-green-300 text-sm">
                Signed in as <strong>{user.email}</strong>
              </p>
            </div>

            <div className="border border-border p-6">
              <h2 className="font-serif-display text-2xl font-medium mb-4">Your Stored Data</h2>
              <div className="space-y-4">
                {userData.name && (
                  <div>
                    <p className="text-sm text-muted-foreground">Name</p>
                    <p className="font-medium">{userData.name}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium">{userData.email}</p>
                </div>
                {userData.original_cv_download_url && (
                  <div>
                    <p className="text-sm text-muted-foreground">CV File</p>
                    <p className="font-medium">Uploaded</p>
                  </div>
                )}
                {userData.job_title && (
                  <div>
                    <p className="text-sm text-muted-foreground">Current Role</p>
                    <p className="font-medium">{userData.job_title}</p>
                  </div>
                )}
                {userData.location && (
                  <div>
                    <p className="text-sm text-muted-foreground">Location</p>
                    <p className="font-medium">{userData.location}</p>
                  </div>
                )}
                {userData.target_roles && (
                  <div>
                    <p className="text-sm text-muted-foreground">Target Roles</p>
                    <p className="font-medium">{Array.isArray(userData.target_roles) ? userData.target_roles.join(', ') : userData.target_roles}</p>
                  </div>
                )}
                {userData.target_locations && (
                  <div>
                    <p className="text-sm text-muted-foreground">Target Locations</p>
                    <p className="font-medium">{Array.isArray(userData.target_locations) ? userData.target_locations.join(', ') : userData.target_locations}</p>
                  </div>
                )}
                {userData.created_at && (
                  <div>
                    <p className="text-sm text-muted-foreground">Submitted On</p>
                    <p className="font-medium">{new Date(userData.created_at).toLocaleString()}</p>
                  </div>
                )}
                {userData.status && (
                  <div>
                    <p className="text-sm text-muted-foreground">Status</p>
                    <p className="font-medium capitalize">{userData.status}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Your Career Profile (Track 23) */}
            <div className="border border-border p-6" data-testid="career-profile-card">
              <h2 className="font-serif-display text-2xl font-medium mb-4">Your Career Profile</h2>
              {accountOverviewLoading && (
                <div className="space-y-3 animate-pulse" data-testid="career-profile-loading">
                  <div className="h-4 w-1/2 rounded bg-border" />
                  <div className="h-4 w-1/3 rounded bg-border" />
                </div>
              )}
              {!accountOverviewLoading && accountOverviewError && (
                <div data-testid="career-profile-error">
                  <p className="text-sm text-red-600 dark:text-red-400">{safeManageDataText(accountOverviewError)}</p>
                  <button
                    type="button"
                    onClick={fetchAccountOverview}
                    className="mt-2 text-sm font-medium text-foreground underline"
                    data-testid="career-profile-retry-btn"
                  >
                    Retry
                  </button>
                </div>
              )}
              {!accountOverviewLoading && !accountOverviewError && accountOverview && (
                <div className="space-y-5">
                  <div>
                    <p className="text-sm text-muted-foreground">Master CV</p>
                    <p className="font-medium">
                      {!accountOverview.master_cv?.has_master_cv
                        ? 'No saved Master CV yet'
                        : accountOverview.master_cv.cv_review_status === 'confirmed'
                          ? 'Confirmed and ready to reuse'
                          : 'Needs your review'}
                    </p>
                    {accountOverview.master_cv?.has_master_cv && accountOverview.master_cv.cv_review_status === 'confirmed' && accountOverview.master_cv.confirmed_at && (
                      <p className="text-sm text-muted-foreground">
                        Confirmed {new Date(accountOverview.master_cv.confirmed_at).toLocaleDateString()}
                      </p>
                    )}
                    {accountOverview.master_cv?.has_master_cv && accountOverview.master_cv.cv_review_status !== 'confirmed' && accountOverview.master_cv.last_saved_at && (
                      <p className="text-sm text-muted-foreground">
                        Saved {new Date(accountOverview.master_cv.last_saved_at).toLocaleDateString()}
                      </p>
                    )}
                    {!accountOverview.master_cv?.has_master_cv && (
                      <Link to="/find-jobs/check-fit" className="mt-2 inline-block text-sm font-medium text-foreground underline">
                        Add your CV
                      </Link>
                    )}
                  </div>
                  <div className="space-y-1 text-sm">
                    <p>{accountOverview.documents?.cv_generated_count ?? 0} CVs generated</p>
                    <p>{accountOverview.documents?.cover_letter_generated_count ?? 0} cover letters generated</p>
                    <p>{accountOverview.applications?.marked_applied_count ?? 0} applications marked</p>
                  </div>
                </div>
              )}
            </div>

            {/* Recent Activity (Track 24) */}
            <div className="border border-border p-6" data-testid="recent-activity-card">
              <h2 className="font-serif-display text-2xl font-medium mb-4">Recent Activity</h2>
              {activityLoading && (
                <div className="space-y-3 animate-pulse" data-testid="recent-activity-loading">
                  <div className="h-4 w-full rounded bg-border" />
                  <div className="h-4 w-full rounded bg-border" />
                </div>
              )}
              {!activityLoading && activityError && (
                <div data-testid="recent-activity-error">
                  <p className="text-sm text-red-600 dark:text-red-400">{safeManageDataText(activityError)}</p>
                  <button
                    type="button"
                    onClick={fetchActivity}
                    className="mt-2 text-sm font-medium text-foreground underline"
                    data-testid="recent-activity-retry-btn"
                  >
                    Retry
                  </button>
                </div>
              )}
              {!activityLoading && !activityError && activityEvents && activityEvents.length === 0 && (
                <p className="text-sm text-muted-foreground" data-testid="recent-activity-empty">No activity yet</p>
              )}
              {!activityLoading && !activityError && activityEvents && activityEvents.length > 0 && (
                <ul className="space-y-3" data-testid="recent-activity-list">
                  {activityEvents.map((event, index) => (
                    <li key={`${event.activity_type}-${event.job_result_id || 'none'}-${index}`} className="text-sm">
                      <p className="font-medium">{event.display_label}</p>
                      <p className="text-muted-foreground">{new Date(event.occurred_at).toLocaleString()}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Recruiter Privacy (Track 26) */}
            <RecruiterPrivacySection
              consent={recruiterConsent}
              isLoading={recruiterConsentLoading}
              error={recruiterConsentError}
              onRetry={fetchRecruiterConsent}
              pendingKeys={pendingRecruiterConsentKeys}
              saveErrors={recruiterConsentSaveErrors}
              savedKey={savedRecruiterConsentKey}
              onToggle={handleRecruiterConsentToggle}
            />

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={handleExport}
                id="data-export"
                className="btn-secondary flex-1 inline-flex items-center justify-center gap-2"
                data-testid="gdpr-export-btn"
              >
                <Download className="w-4 h-4" />
                Export My Data
              </Button>
              <Button
                onClick={() => setShowDeleteConfirm(true)}
                id="data-delete"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 transition-colors"
                data-testid="gdpr-delete-btn"
              >
                <Trash2 className="w-4 h-4" />
                Delete All My Data
              </Button>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mt-6 p-4 border border-red-500/30 bg-red-500/5">
            <p className="text-red-600 dark:text-red-400">{safeManageDataText(error)}</p>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" aria-labelledby="gdpr-delete-confirm-heading">
            <div className="bg-background border border-border p-8 max-w-md w-full">
              <div className="flex items-center gap-3 mb-4">
                <AlertTriangle className="w-6 h-6 text-red-500" />
                <h3 id="gdpr-delete-confirm-heading" className="font-serif-display text-2xl font-medium">Confirm Deletion</h3>
              </div>
              <p className="text-muted-foreground mb-6">
                This action is <strong>permanent and irreversible</strong>. All your personal data,
                including your CV file and account, will be permanently deleted from our systems.
              </p>
              <div className="flex gap-4">
                <Button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="btn-secondary flex-1"
                  disabled={isDeleting}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDelete}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white px-6 py-3 transition-colors"
                  disabled={isDeleting}
                  data-testid="gdpr-confirm-delete-btn"
                >
                  {isDeleting ? 'Deleting...' : 'Yes, Delete Everything'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Info Section */}
        <div className="mt-12 p-6 bg-surface border border-border">
          <h3 className="font-serif-display text-xl font-medium mb-3">Your Rights Under GDPR</h3>
          <ul className="text-muted-foreground space-y-2">
            <li>* <strong>Access:</strong> View all data we have about you</li>
            <li>* <strong>Export:</strong> Download your data in machine-readable format</li>
            <li>* <strong>Erasure:</strong> Permanently delete all your data</li>
            <li>* <strong>Retention:</strong> You can request deletion of your account data at any time</li>
          </ul>
          <p className="mt-4 text-sm text-muted-foreground">
            For more information, read our{' '}
            <Link to="/privacy-policy" className="text-foreground underline hover:no-underline">
              Privacy Policy
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
